import { useMemo, useRef, useState } from 'react';
import { Check, Search, Zap } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { fieldClass } from '../../components/ui/form-classes';
import {
  QUICK_TASK_REFUSALS,
  driverCandidates,
  quickTaskRefusal,
  searchDriverCandidates,
} from '../../core/driver-workload';
import type { DriverCandidate } from '../../core/driver-workload';
import { QUICK_TASK_DESCRIPTION_MAX } from '../../types/quick-task';
import type { QuickDriverTaskDraft } from '../../types/quick-task';
import type { AssignmentIndex } from '../../core/effective-status';
import type { Soldier } from '../../types/fleet';

interface QuickTaskModalProps {
  soldiers: readonly Soldier[];
  /** Who is out right now — the shell derived it once for every screen. */
  assignments: AssignmentIndex;
  onClose: () => void;
  /** Files the journey. Rejecting is the caller's way of saying it did not. */
  onSubmit: (draft: QuickDriverTaskDraft) => Promise<unknown>;
}

/** How each availability reads on the dropdown row. */
const AVAILABILITY_CLASSES: Record<string, string> = {
  available: 'text-emerald-700',
  on_mission: 'text-brand-700',
  leave: 'text-amber-700',
  unavailable: 'text-rose-700',
};

/**
 * ⚡ רישום משימה מהירה — the two-field form.
 *
 * Two fields, and the discipline is the point: this is the form a מ"פ fills in
 * while a driver is still standing in front of them, and every extra box is a
 * journey that ends up not being logged at all. A date, a vehicle, a type and a
 * contact all belong to a dispatched task, and the board already has a form for
 * one — this is deliberately not a second task form, and the mission it files
 * never reaches the dispatch sheet.
 *
 * The driver field is a real combobox rather than a `<select>`: the roster runs
 * to dozens of names, and the same search the roster screen uses
 * (`matchesSoldierSearch`) means typing a personal number or a platoon finds
 * somebody exactly as it does there. The list is ordered available-first, and
 * every row says where that driver is — so an operator about to credit a mission
 * to somebody who is on leave can see it before they tap.
 */
export function QuickTaskModal({ soldiers, assignments, onClose, onSubmit }: QuickTaskModalProps) {
  const [now] = useState(() => Date.now());
  const [query, setQuery] = useState('');
  const [driver, setDriver] = useState<DriverCandidate | null>(null);
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const descriptionRef = useRef<HTMLInputElement>(null);

  const candidates = useMemo(
    () => driverCandidates(soldiers, assignments, now),
    [soldiers, assignments, now],
  );
  const matches = useMemo(
    () => searchDriverCandidates(candidates, soldiers, query),
    [candidates, soldiers, query],
  );

  const draft: QuickDriverTaskDraft = { driverId: driver?.soldierId ?? '', description };
  const refusal = quickTaskRefusal(draft);

  const choose = (candidate: DriverCandidate) => {
    setDriver(candidate);
    setQuery('');
    setError(null);
    // The whole form is two taps; the second field takes the focus itself.
    descriptionRef.current?.focus();
  };

  const submit = async () => {
    if (refusal) {
      setError(QUICK_TASK_REFUSALS[refusal]);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSubmit(draft);
      onClose();
    } catch (err) {
      setError((err as Error).message || 'רישום המשימה נכשל');
      setSaving(false);
    }
  };

  return (
    <Modal
      title="⚡ רישום משימה מהירה"
      subtitle="שני שדות בלבד — נהג ומה בוצע. הרישום נשמר מיד ומתעדכן בגרף."
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 rounded-xl border border-line text-sm"
          >
            ביטול
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={Boolean(refusal) || saving}
            className="h-10 px-5 rounded-xl bg-brand-500 text-white text-sm font-semibold disabled:opacity-40 flex items-center gap-2"
          >
            <Zap size={16} aria-hidden />
            {saving ? 'נרשם…' : 'רישום המשימה'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {/* ── 1. The driver ─────────────────────────────────────────── */}
        <div>
          <label htmlFor="quick-task-driver" className="block text-sm font-medium mb-1.5">
            נהג
          </label>

          {driver ? (
            <div className="flex items-center justify-between gap-3 rounded-xl border border-brand-300 bg-brand-50 px-3 h-10">
              <span className="min-w-0 truncate text-sm font-semibold">{driver.displayName}</span>
              <button
                type="button"
                onClick={() => setDriver(null)}
                className="shrink-0 text-xs text-brand-700 underline"
              >
                החלפה
              </button>
            </div>
          ) : (
            <>
              <div className="relative">
                <Search
                  size={16}
                  aria-hidden
                  className="absolute top-3 start-3 text-ink/40 pointer-events-none"
                />
                <input
                  id="quick-task-driver"
                  type="text"
                  role="combobox"
                  aria-expanded="true"
                  aria-controls="quick-task-driver-list"
                  autoComplete="off"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="חיפוש לפי שם, מספר אישי או מחלקה"
                  className={`${fieldClass} ps-9`}
                />
              </div>

              <ul
                id="quick-task-driver-list"
                role="listbox"
                aria-label="נהגים"
                className="mt-2 max-h-56 overflow-y-auto overscroll-contain rounded-xl border border-line divide-y divide-line"
              >
                {matches.length === 0 ? (
                  <li className="p-3 text-sm text-ink/60">לא נמצא נהג מתאים</li>
                ) : (
                  matches.map((candidate) => (
                    <li key={candidate.soldierId}>
                      <button
                        type="button"
                        role="option"
                        aria-selected="false"
                        onClick={() => choose(candidate)}
                        className="w-full text-right px-3 py-2 hover:bg-brand-50 flex items-center justify-between gap-3"
                      >
                        <span className="min-w-0 truncate text-sm">{candidate.displayName}</span>
                        <span
                          className={`shrink-0 text-xs ${
                            AVAILABILITY_CLASSES[candidate.availability] ?? 'text-ink/60'
                          }`}
                        >
                          {candidate.availabilityLabel}
                        </span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </>
          )}
        </div>

        {/* ── 2. What they did ──────────────────────────────────────── */}
        <div>
          <label htmlFor="quick-task-description" className="block text-sm font-medium mb-1.5">
            תיאור המשימה
          </label>
          <input
            id="quick-task-description"
            ref={descriptionRef}
            type="text"
            value={description}
            maxLength={QUICK_TASK_DESCRIPTION_MAX}
            onChange={(event) => setDescription(event.target.value)}
            onKeyDown={(event) => {
              // One tap to submit: the second field's Enter is the button.
              if (event.key === 'Enter' && !refusal && !saving) void submit();
            }}
            placeholder="נסיעה לקצין עיר"
            className={fieldClass}
          />
          <p className="text-xs text-ink/50 mt-1">
            שורה אחת — עד {QUICK_TASK_DESCRIPTION_MAX} תווים.
          </p>
        </div>

        {error ? (
          <p className="text-sm rounded-xl border border-rose-300 bg-rose-50 text-rose-700 p-3">
            {error}
          </p>
        ) : null}

        {!refusal && !error ? (
          <p className="text-sm text-emerald-700 flex items-center gap-1.5">
            <Check size={16} aria-hidden />
            מוכן לרישום — המשימה תיזקף ל{driver?.name} ותופיע בגרף מיד.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
